import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Rocket, Users, Sparkles, BadgeCheck, Award } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { IMAGES } from "../config";

const FILTERS = ["Tutti", "IA Performance", "Creator Growth", "Enterprise Tech"];

const PROJECTS = [
    {
        id: 1,
        title: "NovaX8",
        category: "Enterprise Tech",
        image: IMAGES.aiBrain,
        description:
            "Innovazione, tecnologia e nuove opportunità per Creator e aziende: il progetto che unisce strumenti digitali avanzati e visione strategica per far crescere il tuo business.",
        metrics: [
            { icon: Rocket, label: "Innovazione e tecnologia" },
            { icon: Users, label: "Opportunità per Creator e aziende" },
        ],
    },
    {
        id: 2,
        title: "SKAY1",
        category: "IA Performance",
        image: IMAGES.aiWave,
        description:
            "Intelligenza Artificiale Performance, cashback e marketing innovativo: un ecosistema che crea valore concreto per consumatori e attività commerciali.",
        metrics: [
            { icon: Sparkles, label: "IA Performance applicata" },
            { icon: BadgeCheck, label: "Cashback e marketing innovativo" },
        ],
    },
    {
        id: 3,
        title: "Creator Plus Maria Fasanella",
        category: "Creator Growth",
        image: IMAGES.portraitCard,
        description:
            "Il mio progetto dedicato ai Creator: crescita professionale, formazione e collaborazione con le aziende per trasformare la passione in una carriera solida.",
        metrics: [
            { icon: Award, label: "Formazione e crescita professionale" },
            { icon: Users, label: "Collaborazione con le aziende" },
        ],
    },
];

export const Projects = () => {
    const [filter, setFilter] = useState("Tutti");
    const visible = PROJECTS.filter(
        (p) => filter === "Tutti" || p.category === filter
    );

    return (
        <section id="progetti" data-testid="projects-section" className="py-20 sm:py-28">
            <div className="mx-auto max-w-6xl px-5 sm:px-8">
                <Reveal className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
                    <div className="max-w-xl">
                        <SectionLabel>I miei progetti</SectionLabel>
                        <h2
                            data-testid="projects-heading"
                            className="mt-5 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#03045E]"
                        >
                            Progetti che portano
                            <span className="text-gradient-azure"> risultati misurabili.</span>
                        </h2>
                    </div>
                </Reveal>

                <Reveal delay={0.1}>
                    <div data-testid="projects-filters" className="mt-8 flex flex-wrap gap-2.5">
                        {FILTERS.map((f) => (
                            <button
                                key={f}
                                data-testid={`filter-${f.toLowerCase().replace(/ /g, "-")}`}
                                onClick={() => setFilter(f)}
                                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                                    filter === f
                                        ? "bg-[#03045E] text-white shadow-lg shadow-sky-900/20"
                                        : "bg-white text-slate-600 border border-slate-200 hover:border-[#48CAE4] hover:text-[#0077B6]"
                                }`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <motion.div layout className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    <AnimatePresence mode="popLayout">
                        {visible.map((p, i) => (
                            <motion.article
                                layout
                                key={p.id}
                                data-testid={`project-card-${p.id}`}
                                initial={{ opacity: 0, y: 28, scale: 0.97 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.96 }}
                                transition={{ duration: 0.5, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                                className="group overflow-hidden rounded-3xl bg-white border border-slate-200/80 shadow-xl shadow-sky-900/5 hover:shadow-2xl hover:shadow-sky-500/15 transition-shadow duration-500"
                            >
                                <div className="relative h-48 overflow-hidden">
                                    <img
                                        src={p.image}
                                        alt={p.title}
                                        className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#03045E]/60 to-transparent" />
                                    <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-[11px] font-bold tracking-wide uppercase bg-white/90 text-[#0077B6] backdrop-blur-sm">
                                        {p.category}
                                    </span>
                                </div>
                                <div className="p-6">
                                    <h3 className="font-display text-lg sm:text-xl font-bold text-[#03045E] group-hover:text-[#0077B6] transition-colors duration-300">
                                        {p.title}
                                    </h3>
                                    <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">
                                        {p.description}
                                    </p>
                                    <div className="mt-5 pt-4 border-t border-slate-100 space-y-2">
                                        {p.metrics.map((m) => (
                                            <div key={m.label} className="flex items-center gap-2.5 text-xs font-semibold text-slate-700">
                                                <m.icon className="h-4 w-4 text-[#0096C7]" />
                                                {m.label}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>
        </section>
    );
};
